import { useEffect, useMemo, useRef, useState } from 'react';

function defaultLabel(item) {
  if (!item) return '';
  return item.label ?? item.name ?? item.value ?? item.fio ?? '';
}

function defaultKey(item) {
  if (!item) return '';
  return item.id ?? item.value ?? item.key ?? defaultLabel(item);
}

export function UiAutosuggest({
  label,
  value,
  onChange,
  suggestions = [],
  onSelect,
  placeholder = 'Начните вводить...',
  clearable = true,
  onClear,
  className = '',
  style,
  error,
  emptyText = 'Ничего не найдено',
  getOptionLabel = defaultLabel,
  getOptionKey = defaultKey,
  disabled = false,
}) {
  const rootRef = useRef(null);
  const [open, setOpen] = useState(false);

  const hasQuery = String(value ?? '').trim().length > 0;
  const hasSuggestions = suggestions.length > 0;

  const shouldOpen = useMemo(() => hasQuery && (hasSuggestions || emptyText), [hasQuery, hasSuggestions, emptyText]);

  useEffect(() => {
    setOpen(shouldOpen);
  }, [shouldOpen]);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (!rootRef.current?.contains(e.target)) {
        setOpen(false);
      }
    };
    const onEsc = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onEsc);
    };
  }, [open]);

  return (
    <label className={`ui-field ${className}`.trim()} style={style} ref={rootRef}>
      {label ? <span className="ui-field__label">{label}</span> : null}
      <div className="ui-autosuggest">
        <input
          value={value}
          onChange={(e) => onChange?.(e.target.value)}
          placeholder={placeholder}
          className="ui-control ui-autosuggest__input"
          disabled={disabled}
        />
        {clearable && hasQuery ? (
          <button
            type="button"
            className="ui-autosuggest__clear"
            aria-label="Очистить"
            onClick={() => (onClear ? onClear() : onChange?.(''))}
          >
            ×
          </button>
        ) : null}
        {open ? (
          <ul className="ui-autosuggest__list">
            {hasSuggestions ? (
              suggestions.map((item) => (
                <li
                  key={String(getOptionKey(item))}
                  className="ui-autosuggest__item"
                  style={{ whiteSpace: 'pre-line' }}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => onSelect?.(item)}
                >
                  {getOptionLabel(item)}
                </li>
              ))
            ) : (
              <li className="ui-autosuggest__empty">{emptyText}</li>
            )}
          </ul>
        ) : null}
      </div>
      {error ? <span className="form-error">{error}</span> : null}
    </label>
  );
}
